import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import { ResourceTypeEnum } from "../../common/enums/ResourceTypeEnum";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { ITankStyle } from "../../common/models/TankStyle";
import { RouteFromObjectType } from "../../common/utilities/RouteFromObjectType";
import { IOfflineDevice, IOfflineTank, IOfflineTankBattery } from "../offline-data-service/Models";
import OfflineDataService from "../offline-data-service/OfflineDataService";
import restService from "../rest-service/RestService";
import { IRunTicketModel } from "./Models";

const RunTicketDataService = {

    getRunTickets: async function (
        customerID: number,
        groupID: number,
        eLynxObject: IeLynxObject,
        offline: boolean,
        timestamp?: Date,
        signal?: AbortSignal
    ): Promise<Array<IRunTicketModel>> {

        if (!offline) {
            const path = `/groups/${groupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/runTickets`;

            const queryParams =
            {
                dateTime: timestamp
            };

            const response = await restService.get<Array<IRunTicketModel>, {
                dateTime: Date | undefined;
            }>(path, signal, queryParams);

            return response.data;

        } else {
            let runTickets = await OfflineDataService.getPendingRunTickets(customerID);
            const tankStyles = await OfflineDataService.getTankStyles(customerID);
            //User Story 53044: Research - 2025 Q2 - Review GAS report
            //let tanks = new Array<IRunTicketModel.Tank>();

            if (eLynxObject.objectType === eLynxObjectTypeEnum.Tank) {
                //User Story 53044: Research - 2025 Q2 - Review GAS report
                //const tank = await OfflineDataService.getObject(customerID, eLynxObject, ObjectFeaturesEnum.ManualEntry) as IOfflineTank;
                //tanks = [_toTankModel(tank, tankStyles)];
                runTickets = runTickets.filter(o => o.tank.id === eLynxObject.id);

            } else if (eLynxObject.objectType === eLynxObjectTypeEnum.Device) {
                const device = await OfflineDataService.getObject(customerID, eLynxObject, ObjectFeaturesEnum.ManualEntry, { includeTanks: true }) as IOfflineDevice;
                //User Story 53044: Research - 2025 Q2 - Review GAS report
                const tanks = device.tanks?.map(tank => _toTankModel(tank, tankStyles)) ?? new Array<IRunTicketModel.Tank>();

                let filtered = tanks.reduce((result: IRunTicketModel[], currentValue) => {
                    return result.concat(runTickets.filter(o => o.tank.id === currentValue.id));
                }, []);

                runTickets = filtered;

            } if (eLynxObject.objectType === eLynxObjectTypeEnum.TankBattery) {
                const tankBattery = await OfflineDataService.getObject(customerID, eLynxObject, ObjectFeaturesEnum.ManualEntry, { includeTanks: true }) as IOfflineTankBattery;
                //User Story 53044: Research - 2025 Q2 - Review GAS report
                const tanks = tankBattery.tanks?.map(tank => _toTankModel(tank, tankStyles)) ?? new Array<IRunTicketModel.Tank>();
                let filtered = tanks.reduce((result: IRunTicketModel[], currentValue) => {
                    return result.concat(runTickets.filter(o => o.tank.id === currentValue.id));
                }, []);

                runTickets = filtered;

            }

            return runTickets;
        }
    },

    getRunTicketTanks: async function (
        customerID: number,
        groupID: number,
        eLynxObject: IeLynxObject,
        offline: boolean,
        signal?: AbortSignal
    ): Promise<Array<IRunTicketModel.Tank>> {

        if (!offline) {
            const path = `/groups/${groupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/runTicketTanks`;

            const response = await restService.get<Array<IRunTicketModel.Tank>>(path, signal);

            return response.data;

        } else {
            const tankStyles = await OfflineDataService.getTankStyles(customerID);
            let tanks = new Array<IRunTicketModel.Tank>();

            if (eLynxObject.objectType === eLynxObjectTypeEnum.Tank) {
                const tank = await OfflineDataService.getObject(customerID, eLynxObject, ObjectFeaturesEnum.ManualEntry) as IOfflineTank;
                tanks = [_toTankModel(tank, tankStyles)];

            } else if (eLynxObject.objectType === eLynxObjectTypeEnum.Device) {
                const device = await OfflineDataService.getObject(customerID, eLynxObject, ObjectFeaturesEnum.ManualEntry, { includeTanks: true }) as IOfflineDevice;
                tanks = device.tanks?.map(tank => _toTankModel(tank, tankStyles)) ?? new Array<IRunTicketModel.Tank>();

            } if (eLynxObject.objectType === eLynxObjectTypeEnum.TankBattery) {
                const tankBattery = await OfflineDataService.getObject(customerID, eLynxObject, ObjectFeaturesEnum.ManualEntry, { includeTanks: true }) as IOfflineTankBattery;
                tanks = tankBattery.tanks?.map(tank => _toTankModel(tank, tankStyles)) ?? new Array<IRunTicketModel.Tank>();
            }

            return tanks;
        }
    },

    saveRunTicket: async function (
        customerID: number,
        groupID: number,
        runTicket: IRunTicketModel,
        offline: boolean,
        signal?: AbortSignal

    ): Promise<void> {

        if (!offline) {
            const path: string = `groups/${groupID}/tanks/${runTicket.tank.id}/runtickets`;

            await restService.post<IRunTicketModel, IRunTicketModel>(path, runTicket, signal);

        } else {
            await OfflineDataService.saveRunTickets(customerID, [runTicket]);
        }

    },

    // This is for offline data support    
    saveRunTickets: async function (
        customerID: number,
        runTickets: Array<IRunTicketModel>,
        offline: boolean,
        signal?: AbortSignal
    ): Promise<void> {

        if (!offline) {

            const path: string = "runTickets";
            await restService.post<Array<IRunTicketModel>, Array<IRunTicketModel>>(path, runTickets, signal);

        } else {
            await OfflineDataService.saveRunTickets(customerID, runTickets);
        }
    },

    deleteRunTicket: async function (
        customerID: number,
        groupID: number,
        runTicket: IRunTicketModel,
        offline: boolean,
        signal?: AbortSignal
    ): Promise<void> {

        if (!offline) {

            const path: string = `groups/${groupID}/tanks/${runTicket.tank.id}/runtickets/${runTicket.id}`;
            await restService.delete<number>(path, signal);

        } else {
            await OfflineDataService.deletePendingRunTicket(customerID, runTicket);
        }

    }
};

function _toTankModel(tank: IOfflineTank, tankStyles: Array<ITankStyle>): IRunTicketModel.Tank {
    const tankStyle = tankStyles.find(ts => ts.id === tank.tankStyleID) as ITankStyle;
    return {
        id: tank.id,
        displayText: tank.name,
        resourceType: tank.subTypeID === 1 ? ResourceTypeEnum.Oil : ResourceTypeEnum.Water,
        isDualPhase: tank.dualPhase,
        // tankStyle: {
        //     id: tankStyle?.id as number,
        //     inchesMultiplier: tankStyle?.inchesMultiplier as number,

        // }
        tankStyle: {...tankStyle}
    };
}

export default RunTicketDataService;
